import { useFFRestApi } from '@/composables/useApi';

export interface CityState {
  city: string;
  state: string;
}

export interface GeocodedCityState extends CityState {
  coordinates: { lat: number; lng: number };
}

/** Whether a city belongs to a state; `unknown` when the lookup itself failed. */
export type CityStatePairing = 'valid' | 'invalid' | 'unknown';

interface StatesForCity {
  city: string;
  states: string[];
}

export class GeolocationPersistence {
  async fetchCityStateFromZip(zip: string): Promise<CityState | null> {
    const { data, error } = await useFFRestApi(
      `/geolocation/zip/${encodeURIComponent(zip)}`,
    ).json<CityState>();

    if (error.value || !data.value?.city || !data.value?.state) {
      return null;
    }

    return data.value;
  }

  /**
   * The point ff-rest pins a lives_near job to for this city/state — resolved by the same
   * lookup and cache the save uses, so a preview never disagrees with sourcing.
   */
  async geocodeCityState(city: string, state: string): Promise<GeocodedCityState | null> {
    const query = new URLSearchParams({ city, state }).toString();
    const { data, error } = await useFFRestApi(
      `/geolocation/geocode?${query}`,
    ).json<GeocodedCityState>();

    if (error.value || !data.value?.coordinates) {
      return null;
    }

    return data.value;
  }

  /**
   * Same lookup, read as a yes/no on the pairing. Only a 404 (no such city in that state) is
   * `invalid`; a network or server failure is `unknown` so callers keep what the user typed.
   */
  async checkCityStatePairing(city: string, state: string): Promise<CityStatePairing> {
    const query = new URLSearchParams({ city, state }).toString();
    const { data, response } = await useFFRestApi(
      `/geolocation/geocode?${query}`,
    ).json<GeocodedCityState>();

    if (data.value?.coordinates) return 'valid';
    if (response.value?.status === 404) return 'invalid';
    return 'unknown';
  }

  /** USPS codes of the states that have a city by this name; empty when unknown or the lookup failed. */
  async fetchStatesForCity(city: string): Promise<string[]> {
    const query = new URLSearchParams({ city }).toString();
    const { data, error } = await useFFRestApi(
      `/geolocation/states?${query}`,
    ).json<StatesForCity>();

    const states = data.value?.states;
    if (error.value || !Array.isArray(states)) {
      return [];
    }

    return states;
  }
}
